import { getPayloadSizeMultiplier } from './widgetCarouselText';

export function buildWidgetCarouselCreateFormData(form) {
    const payload = buildWidgetCarouselPayload(form);
    const formData = new FormData();

    formData.append('payload', JSON.stringify(payload));

    if (
        form.global_background_image &&
        form.global_background_image.file instanceof File &&
        payload.image_key
    ) {
        formData.append(`images[${payload.image_key}]`, form.global_background_image.file);
    }

    if (Array.isArray(form.products)) {
        form.products.forEach((product, productIndex) => {
            const payloadProduct =
                payload.products &&
                payload.products[productIndex]
                    ? payload.products[productIndex]
                    : null;

            if (!payloadProduct) {
                return;
            }

            if (product.image && product.image.file instanceof File && payloadProduct.image_key) {
                formData.append(`images[${payloadProduct.image_key}]`, product.image.file);
            }

            appendBadgeFiles(formData, product, payloadProduct);
        });
    }

    return formData;
}

export function buildWidgetCarouselPayload(form) {
    return {
        is_active: !!form.is_active,
        is_published: !!form.is_published,
        type: form.type === 'offers' ? 'offers' : 'benefits',
        widget_id: form.widget_id || '',
        tracking: form.tracking || '',
        priority:
            Number(form.priority) >= 80 &&
            Number(form.priority) <= 200 &&
            Number(form.priority) % 5 === 0
                ? Number(form.priority)
                : 100,
        available_start_date: form.available_start_date || '',
        available_end_date: form.available_end_date || '',

        ratio: Number(form.ratio) || 2,
        auto_scroll: !!form.auto_scroll,
        display_page_indicator: !!form.display_page_indicator,

        title: {
            ro: getSafeI18nValue(form, 'title', 'ro'),
            en: getSafeI18nValue(form, 'title', 'en')
        },

        subtitle: {
            ro: getSafeI18nValue(form, 'subtitle', 'ro'),
            en: getSafeI18nValue(form, 'subtitle', 'en')
        },

        footer: {
            ro: getSafeI18nValue(form, 'footer', 'ro'),
            en: getSafeI18nValue(form, 'footer', 'en')
        },

        image_key:
            form.global_background_image &&
            form.global_background_image.file instanceof File
                ? 'widget_main_image'
                : undefined,

        remove_image: !!(
            form.global_background_image &&
            form.global_background_image.removed &&
            !(form.global_background_image.file instanceof File)
        ),

        products: Array.isArray(form.products)
            ? form.products.map((product, productIndex) => buildProductPayload(product, productIndex))
            : []
    };
}

function buildProductPayload(product, productIndex) {
    const safeProduct = product || {};
    const productImageKey = buildProductImageKey(safeProduct, productIndex);

    return {
        source_type: safeProduct.source_type === 'offers' ? 'offers' : 'benefits',
        product_id: safeProduct.product_id != null ? safeProduct.product_id : null,
        product_name: safeProduct.product_name || '',
        product_name_en: safeProduct.product_name_en || '',
        price: safeProduct.price != null ? String(safeProduct.price) : '',
        discount_price: safeProduct.discount_price != null ? String(safeProduct.discount_price) : '',
        user_discount: safeProduct.user_discount != null ? String(safeProduct.user_discount) : '',
        discount: safeProduct.discount != null ? String(safeProduct.discount) : '',
        value: safeProduct.value != null ? String(safeProduct.value) : '',
        brand_name: safeProduct.brand_name || '',

        priority: safeProduct.priority != null ? Number(safeProduct.priority) : productIndex + 1,
        is_active: typeof safeProduct.is_active === 'boolean' ? safeProduct.is_active : true,
        reason: safeProduct.reason || '',
        image_key: productImageKey,
        remove_image: !!(
            safeProduct.image &&
            safeProduct.image.removed &&
            !(safeProduct.image.file instanceof File)
        ),

        corner_radius: Number(safeProduct.corner_radius) || 0,
        use_d_shape: !!safeProduct.use_d_shape,
        spacing: Number(safeProduct.spacing) || 0,
        background_color: safeProduct.background_color || '',

        margins: {
            top: getNestedNumber(safeProduct, 'margins', 'top', 0),
            left: getNestedNumber(safeProduct, 'margins', 'left', 0),
            right: getNestedNumber(safeProduct, 'margins', 'right', 0),
            bottom: getNestedNumber(safeProduct, 'margins', 'bottom', 0)
        },

        paddings: {
            top: getNestedNumber(safeProduct, 'paddings', 'top', 0),
            left: getNestedNumber(safeProduct, 'paddings', 'left', 0),
            right: getNestedNumber(safeProduct, 'paddings', 'right', 0),
            bottom: getNestedNumber(safeProduct, 'paddings', 'bottom', 0)
        },

        items: Array.isArray(safeProduct.items)
            ? safeProduct.items.map((item, itemIndex) =>
                buildProductItemPayload(item, safeProduct, productIndex, itemIndex)
            )
            : []
    };
}

function buildProductItemPayload(item, product, productIndex, itemIndex) {
    const safeItem = item || {};
    const useDefault = !!safeItem.voucher_url_use_default;
    const productId = product && product.product_id != null ? product.product_id : '';
    const sourceType = product && product.source_type ? product.source_type : 'benefits';

    const defaultVoucherUrl = sourceType === 'offers'
        ? `com.upmobil://multiben/offer/${productId}`
        : `com.upmobil://multiben/benefits/voucher/${productId}?is_selected=true`;

    return {
        voucher_url_use_default: useDefault,
        voucher_url: useDefault
            ? defaultVoucherUrl
            : (safeItem.voucher_url || ''),
        scroll_duration: getOptionalPayloadNumber(safeItem.scroll_duration, 1),
        badges: buildBadgesPayload(safeItem.badges || {}, product, productIndex, itemIndex)
    };
}

function buildBadgesPayload(badges, product, productIndex, itemIndex) {
    return {
        top_left: buildBadgeListPayload(badges.top_left, product, productIndex, itemIndex, 'top_left'),
        top_right: buildBadgeListPayload(badges.top_right, product, productIndex, itemIndex, 'top_right'),
        bottom_left: buildBadgeListPayload(badges.bottom_left, product, productIndex, itemIndex, 'bottom_left'),
        bottom_right: buildBadgeListPayload(badges.bottom_right, product, productIndex, itemIndex, 'bottom_right')
    };
}

function buildBadgeListPayload(list, product, productIndex, itemIndex, positionKey) {
    if (!Array.isArray(list)) {
        return [];
    }

    return list
        .map((badge, badgeIndex) =>
            buildBadgePositionPayload(
                badge,
                product,
                productIndex,
                itemIndex,
                positionKey,
                badgeIndex
            )
        )
        .filter(Boolean);
}

function buildBadgePositionPayload(badge, product, productIndex, itemIndex, positionKey, badgeIndex) {
    if (!badge) {
        return null;
    }

    const badgeItems = Array.isArray(badge.items) ? badge.items : [];

    return {
        background_color: badge.background_color || '',
        corner_radius: Number(badge.corner_radius) || 0,
        use_d_shape: !!badge.use_d_shape,
        spacing: Number(badge.spacing) || 0,

        paddings: {
            top: getNestedNumber(badge, 'paddings', 'top', 0),
            left: getNestedNumber(badge, 'paddings', 'left', 0),
            right: getNestedNumber(badge, 'paddings', 'right', 0),
            bottom: getNestedNumber(badge, 'paddings', 'bottom', 0)
        },

        margins: {
            top: getNestedNumber(badge, 'margins', 'top', 0),
            left: getNestedNumber(badge, 'margins', 'left', 0),
            right: getNestedNumber(badge, 'margins', 'right', 0),
            bottom: getNestedNumber(badge, 'margins', 'bottom', 0)
        },

        items: badgeItems.map((badgeItem, badgeItemIndex) =>
            buildBadgeContentItemPayload(
                badgeItem,
                product,
                productIndex,
                itemIndex,
                positionKey,
                badgeIndex,
                badgeItemIndex
            )
        )
    };
}

function buildBadgeContentItemPayload(
    badgeItem,
    product,
    productIndex,
    itemIndex,
    positionKey,
    badgeIndex,
    badgeItemIndex
) {
    const rawTitle = {
        ro: getSafeI18nValue(badgeItem, 'title', 'ro'),
        en: getSafeI18nValue(badgeItem, 'title', 'en')
    };

    return {
        content_mode: badgeItem.content_mode || 'custom',
        product_variable_key: badgeItem.product_variable_key || '',

        left_icon: '',
        left_icon_key: badgeItem.left_icon_file instanceof File
            ? buildBadgeLeftIconKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex)
            : '',
        left_icon_remove: !!(badgeItem.left_icon_remove && !(badgeItem.left_icon_file instanceof File)),

        title_raw: {
            ro: rawTitle.ro,
            en: rawTitle.en
        },

        title: {
            ro: interpolateTemplate(rawTitle.ro, product),
            en: interpolateTemplate(rawTitle.en, product)
        },

        title_color: badgeItem.title_color || '#000000',
        title_size_multiplier: getPayloadSizeMultiplier(badgeItem.title_size_multiplier),

        right_icon: '',
        right_icon_key: badgeItem.right_icon_file instanceof File
            ? buildBadgeRightIconKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex)
            : '',
        right_icon_remove: !!(badgeItem.right_icon_remove && !(badgeItem.right_icon_file instanceof File)),

        image: '',
        image_key: badgeItem.image_file instanceof File
            ? buildBadgeImageKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex)
            : '',
        remove_image: !!(badgeItem.remove_image && !(badgeItem.image_file instanceof File)),

        background_color: badgeItem.background_color || '',
        corner_radius: getOptionalPayloadNumber(badgeItem.corner_radius, 0),
        use_d_shape: !!badgeItem.use_d_shape,

        paddings: {
            top: getNestedNumber(badgeItem, 'paddings', 'top', 0),
            left: getNestedNumber(badgeItem, 'paddings', 'left', 0),
            right: getNestedNumber(badgeItem, 'paddings', 'right', 0),
            bottom: getNestedNumber(badgeItem, 'paddings', 'bottom', 0)
        }
    };
}

function appendBadgeFiles(formData, product, payloadProduct) {
    const sourceItems = Array.isArray(product.items) ? product.items : [];
    const payloadItems = Array.isArray(payloadProduct.items) ? payloadProduct.items : [];

    sourceItems.forEach((productItem, itemIndex) => {
        const payloadItem = payloadItems[itemIndex];

        if (!productItem || !payloadItem || !productItem.badges) {
            return;
        }

        ['top_left', 'top_right', 'bottom_left', 'bottom_right'].forEach((positionKey) => {
            const sourceList = Array.isArray(productItem.badges[positionKey])
                ? productItem.badges[positionKey]
                : [];

            const payloadList =
                payloadItem.badges && Array.isArray(payloadItem.badges[positionKey])
                    ? payloadItem.badges[positionKey]
                    : [];

            sourceList.forEach((badge, badgeIndex) => {
                const payloadBadge = payloadList[badgeIndex];

                if (!badge || !payloadBadge) {
                    return;
                }

                const sourceBadgeItems = Array.isArray(badge.items) ? badge.items : [];
                const payloadBadgeItems = Array.isArray(payloadBadge.items) ? payloadBadge.items : [];

                sourceBadgeItems.forEach((badgeItem, badgeItemIndex) => {
                    const payloadBadgeItem = payloadBadgeItems[badgeItemIndex];

                    if (!badgeItem || !payloadBadgeItem) {
                        return;
                    }

                    if (badgeItem.left_icon_file instanceof File && payloadBadgeItem.left_icon_key) {
                        formData.append(`images[${payloadBadgeItem.left_icon_key}]`, badgeItem.left_icon_file);
                    }

                    if (badgeItem.right_icon_file instanceof File && payloadBadgeItem.right_icon_key) {
                        formData.append(`images[${payloadBadgeItem.right_icon_key}]`, badgeItem.right_icon_file);
                    }

                    if (badgeItem.image_file instanceof File && payloadBadgeItem.image_key) {
                        formData.append(`images[${payloadBadgeItem.image_key}]`, badgeItem.image_file);
                    }
                });
            });
        });
    });
}

function buildProductImageKey(product, productIndex) {
    if (!(product && product.image && product.image.file instanceof File)) {
        return undefined;
    }

    const productId =
        product && product.product_id != null
            ? product.product_id
            : `idx_${productIndex}`;

    return `product_${productId}_image`;
}

function buildBadgeImageKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex) {
    return `badge_p${productIndex}_i${itemIndex}_${positionKey}_b${badgeIndex}_bi${badgeItemIndex}_image`;
}

function buildBadgeLeftIconKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex) {
    return `badge_p${productIndex}_i${itemIndex}_${positionKey}_b${badgeIndex}_bi${badgeItemIndex}_left_icon`;
}

function buildBadgeRightIconKey(productIndex, itemIndex, positionKey, badgeIndex, badgeItemIndex) {
    return `badge_p${productIndex}_i${itemIndex}_${positionKey}_b${badgeIndex}_bi${badgeItemIndex}_right_icon`;
}

function interpolateTemplate(template, product) {
    return String(template || '').replace(/\{\{(.*?)\}\}/g, function (_, rawKey) {
        const key = String(rawKey || '').trim();
        return resolveProductVariable(product, key);
    });
}

function resolveProductVariable(product, variableKey) {
    const safeProduct = product || {};
    const sourceType = safeProduct.source_type || 'benefits';

    const benefitVariables = {
        product_name: safeProduct.product_name || '',
        product_name_en: safeProduct.product_name_en || '',
        price: safeProduct.price || '',
        discount_price: safeProduct.discount_price || '',
        user_discount: safeProduct.user_discount || '',
        discount: safeProduct.discount || '',
        brand_name: safeProduct.brand_name || ''
    };

    const offerVariables = {
        name: safeProduct.product_name || '',
        brand_name: safeProduct.brand_name || '',
        value: safeProduct.value || ''
    };

    const variables = sourceType === 'offers'
        ? offerVariables
        : benefitVariables;

    return variables[variableKey] || '';
}

function getSafeI18nValue(source, field, lang) {
    if (
        source &&
        source[field] &&
        typeof source[field][lang] === 'string'
    ) {
        return source[field][lang];
    }

    return '';
}

// Empty or out-of-range values become null, so the API doesn't send them to UP Ro
function getOptionalPayloadNumber(value, min) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsedValue = Number(value);

    return Number.isNaN(parsedValue) || parsedValue < min ? null : parsedValue;
}

function getNestedNumber(source, parentField, field, fallback) {
    if (
        source &&
        source[parentField] &&
        !Number.isNaN(Number(source[parentField][field]))
    ) {
        return Number(source[parentField][field]);
    }

    return fallback;
}